/**
 * Stop-hook guard, retry budget, and event log for `cai verify --hook`.
 *
 * The bare Stop hook (`cai verify --hook`) had three gaps:
 *   1. It ignored the hook payload entirely — no `session_id`, no `stop_hook_active`.
 *   2. No retry ceiling: every failing Stop exits 2, so a failure the agent can't fix
 *      (flaky test, environment issue) loops forever, burning turns/tokens.
 *   3. No observability — there was no record of how often the hook fired or blocked.
 *
 * This module adds a per-session retry budget and an append-only event log. The budget
 * is the load-bearing mechanism: after N consecutive blocking runs in a session we stop
 * exiting 2 and let the agent stop, with a clear note. `stop_hook_active` is used ONLY
 * to detect re-entrancy (the hook firing because of its own previous block) — we do NOT
 * blanket exit 0 on it, because that would silently defeat back-pressure on the first
 * legitimate failure.
 *
 * State + log live under .cai/.cache (gitignored). Never throws. CAI_NO_TELEMETRY
 * disables the log but NOT the budget (the budget is correctness, not telemetry).
 */
import { appendFileSync, existsSync, mkdirSync, readFileSync, writeFileSync } from "node:fs";
import { dirname, join } from "node:path";

const CACHE_DIR = join(".cai", ".cache");
const STATE_FILE = ".verify-hook-state.json";
const LOG_FILE = "verify-events.jsonl";

/** Default consecutive-block ceiling per session before we stop blocking. */
export const DEFAULT_RETRY_BUDGET = 3;

export interface HookState {
  sessionId: string;
  /** Consecutive blocking runs in this session. */
  retries: number;
}

export type HookAction = "exit2" | "budget-exhausted" | "pass";

export interface HookDecision {
  action: HookAction;
  state: HookState;
  /** Human-readable note for budget-exhausted, surfaced to the agent. */
  note?: string;
}

export interface VerifyEvent {
  ts: number;
  sessionId: string;
  blocked: boolean;
  action: HookAction;
  retries: number;
  stopHookActive: boolean;
  blockingSteps: string[];
}

function isTelemetryDisabled(): boolean {
  return Boolean(process.env.CAI_NO_TELEMETRY);
}

function statePath(projectRoot: string): string {
  return join(projectRoot, CACHE_DIR, STATE_FILE);
}

function logPath(projectRoot: string): string {
  return join(projectRoot, CACHE_DIR, LOG_FILE);
}

/** Read the persisted hook state, or null when absent/unreadable. */
export function readHookState(projectRoot: string): HookState | null {
  const path = statePath(projectRoot);
  if (!existsSync(path)) return null;
  try {
    const parsed = JSON.parse(readFileSync(path, "utf8")) as HookState;
    if (typeof parsed?.sessionId !== "string" || typeof parsed?.retries !== "number") return null;
    return parsed;
  } catch {
    return null;
  }
}

/** Persist the hook state. Never throws. (Budget state, so not gated by CAI_NO_TELEMETRY.) */
export function writeHookState(projectRoot: string, state: HookState): void {
  try {
    const path = statePath(projectRoot);
    mkdirSync(dirname(path), { recursive: true });
    writeFileSync(path, JSON.stringify(state) + "\n", "utf8");
  } catch {
    // never fail the hook over its own state file
  }
}

/**
 * Decide what the hook should do given the current run.
 *
 * @param prev          previously persisted state (null on first run)
 * @param sessionId     hook session id ("nosession" when absent)
 * @param blocked       true when this verify run has blocking failures (regressions/new)
 * @param budget        consecutive-block ceiling
 */
export function decideHookAction(
  prev: HookState | null,
  sessionId: string,
  blocked: boolean,
  budget = DEFAULT_RETRY_BUDGET
): HookDecision {
  // New session ⇒ fresh budget.
  const base: HookState = prev && prev.sessionId === sessionId ? prev : { sessionId, retries: 0 };

  if (!blocked) {
    // Clean (or only pre-existing) — reset the budget and let the agent stop.
    return { action: "pass", state: { sessionId, retries: 0 } };
  }

  const retries = base.retries + 1;
  if (retries > budget) {
    return {
      action: "budget-exhausted",
      state: { sessionId, retries },
      note:
        `cai verify: ${retries - 1} consecutive failing re-engagements in this session ` +
        `exceeded the retry budget (${budget}). Not blocking again — fix the remaining ` +
        `failures manually or run \`cai verify --no-baseline\` to inspect them.`,
    };
  }

  return { action: "exit2", state: { sessionId, retries } };
}

/** Append a verify-hook event to the JSONL log. Never throws; respects CAI_NO_TELEMETRY. */
export function appendVerifyEvent(projectRoot: string, event: Omit<VerifyEvent, "ts">): void {
  if (isTelemetryDisabled()) return;
  try {
    const path = logPath(projectRoot);
    mkdirSync(dirname(path), { recursive: true });
    appendFileSync(path, JSON.stringify({ ts: Date.now(), ...event }) + "\n", "utf8");
  } catch {
    // swallow — observability must not break the hook
  }
}

/** Read all verify-hook events, oldest first. */
export function readVerifyEvents(projectRoot: string): VerifyEvent[] {
  const path = logPath(projectRoot);
  if (!existsSync(path)) return [];
  let raw: string;
  try {
    raw = readFileSync(path, "utf8");
  } catch {
    return [];
  }
  const events: VerifyEvent[] = [];
  for (const line of raw.split("\n")) {
    if (!line) continue;
    try {
      events.push(JSON.parse(line) as VerifyEvent);
    } catch {
      // skip malformed line
    }
  }
  return events;
}

/** Parse the Claude Code hook stdin payload. Best-effort; returns defaults on any failure. */
export function parseHookStdin(raw: string): { sessionId: string; stopHookActive: boolean } {
  try {
    const parsed = JSON.parse(raw) as { session_id?: string; stop_hook_active?: boolean };
    return {
      sessionId: typeof parsed.session_id === "string" && parsed.session_id ? parsed.session_id : "nosession",
      stopHookActive: parsed.stop_hook_active === true,
    };
  } catch {
    return { sessionId: "nosession", stopHookActive: false };
  }
}
